import { ODataV4Adaptor } from "@syncfusion/ej2-data";
import { buildFilter } from "../utils/ODataFilterBuilder";

const operators = {
  greaterthan: "gt",
  greaterthanorequal: "ge",
  lessthan: "lt",
  lessthanorequal: "le",
  equal: "eq",
  notequal: "ne",
  contains: "contains",
  wildcard: "wildcard"
};

export class WebApiV4Adaptor extends ODataV4Adaptor {
  onPredicate(predicate, query) {
    if (!predicate) {
      return super.onPredicate(predicate, query);
    }

    const { field, operator } = predicate;
    let { value } = predicate;

    if (field === "IsExtendedTaxDeadline") {
      value = value.toLowerCase();
      return `ExtendedTaxDeadline ${value === "yes" ? "ne" : "eq"} null`;
    }

    if (field === "FilingStatus") {
      return getFilingStatusFilter(field, operator, value);
    }

    if (field === "Tags" || field === "Collaborators") {
      return getCollectionFilter(field, operator, value);
    }

    return super.onPredicate(predicate, query);
  }

  onEachSort(e) {
    if (e.name === "Tags") {
      e.name = "TagString";
    }
    if (e.name === "Collaborators") {
      e.name = "CollaboratorString";
    }
    // PFT 160288: Number is "{TaskTypeKey}-{Sequence}" stored as text, so a plain
    // sort puts "TAX-9999" above "TAX-16954". Sorting by key, then length, then
    // value gives a natural sort (within a key the prefix length is constant).
    if (e.name === "Number") {
      e.name = ["TaskTypeKey", "length(Number)", "Number"];
    }
    return super.onEachSort(e);
  }

  // Parses legacy OData v3 "/Date(nnn)/" strings into Date objects for any field
  // whose value matches that pattern. The format is OData-specific and cannot
  // occur as a user-entered string, so matching by value shape is safe — unlike
  // dateParse=true which also matches ISO strings like "2022-06-22" that could
  // legitimately be non-date data (e.g. a folder name).
  _parseDateFields(items) {
    if (!Array.isArray(items)) return items;
    const legacyDate = /^\/Date\((-?\d+)\)\/$/;
    return items.map((item) => {
      if (!item || typeof item !== "object") return item;
      const converted = { ...item };
      for (const key of Object.keys(converted)) {
        const val = converted[key];
        if (typeof val === "string") {
          const m = legacyDate.exec(val);
          if (m) converted[key] = new Date(Number(m[1]));
        }
      }
      // Custom field values arrive nested ({ CustomFields: { CF_<id>: value } });
      // flatten them so grid columns can bind by field name (CF_<id>).
      if (
        converted.CustomFields &&
        typeof converted.CustomFields === "object"
      ) {
        Object.assign(converted, converted.CustomFields);
      }
      return converted;
    });
  }

  processResponse(data, ds, query, xhr, request) {
    var pvtData = "pvtData";
    var pvt = request && request[pvtData];

    var count = null;
    var args = {};

    if (request && request.type.toLowerCase() !== "post") {
      if (query && query.isCountRequired) {
        if (data.Count) {
          count = data.Count;
        } else if (data.count) {
          count = data.count;
        }
      }

      if (data.Items) {
        data = this._parseDateFields(data.Items);
      } else if (data.items) {
        data = this._parseDateFields(data.items);
      } else if (Array.isArray(data)) {
        data = this._parseDateFields(data);
      }

      args.count = count;
      args.result = data;

      this.getAggregateResult(pvt, data, args, null, query);
    }

    args.result = args.result || data;

    return !count
      ? args.result
      : { result: args.result, count: args.count, aggregates: args.aggregates };
  }
}

const getFilingStatusFilter = (field, operator, value) => {
  if (field === "FilingStatus") {
    const filingStatuses = {
      1: "Filed",
      2: "NotFiled",
      3: "WaitingForResponse",
      4: "Error",
      5: "Unknown",
      null: null
    };

    if (value === undefined) {
      console.error(
        `WebApiV4Adaptor: unsupported filing status "${value}"; skipping predicate.`
      );
      return "true";
    }

    if (Number.isInteger(value)) {
      value = filingStatuses[value];
    } else {
      const statusStrings = Object.values(filingStatuses);

      // This is the case when searching inside the filter menu.
      if (!statusStrings.includes(value)) {
        const match = statusStrings.find((name) =>
          name
            .toLocaleLowerCase()
            .includes(value?.toString().toLocaleLowerCase())
        );
        if (match) {
          value = match;
        } else value = null;
      }
    }

    return `FilingStatus ${operators[operator]} '${value}'`;
  }
};

const getCollectionFilter = (field, operator, value) => {
  const op = operators[operator];
  if (!op) {
    console.error(
      `WebApiV4Adaptor: unsupported operator "${operator}" on field "${field}"; skipping predicate.`
    );
    return "true";
  } else {
    return buildFilter(field, op, value, {
      OData: { isCollection: true, isLiteral: true }
    });
  }
};
